import { BorderRadius, Color, Spacing } from '@persona-id/design-tokens';
import { DocumentIcon, SearchIcon, XIcon } from '@persona-id/design-tokens/icons';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import SessionClientResourceCenter from 'dashboard/lib/clients/SessionClientResourceCenter';
import Button, { IconButton } from 'design-system/components/Button';
import Input, { InputGroup } from 'design-system/components/Input';
import { Paragraph } from 'design-system/components/Text';
import { SegmentClient } from 'lib/clients/SegmentClient';
import useDebounced from 'lib/hooks/useDebounced';
import { ApiEndpoint } from 'lib/interfaces/api';

const DashboardContactUsSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<HelpCenterArticle[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchErrored, setIsSearchErrored] = useState(false);
  const searchInputRef = useRef(null);

  const performSearch = async (query: string) => {
    const MIN_SEARCH_TERM_LENGTH = 3;
    const MAX_NUM_RESULTS = 6;

    setSearchResults([]);
    setIsSearchErrored(false);

    if (query.length < MIN_SEARCH_TERM_LENGTH) return;
    setIsSearchLoading(true);

    try {
      const searchResponse =
        await SessionClientResourceCenter.getEndpoint<HelpCenterArticleSearchResponse>(
          ApiEndpoint.HelpCenterArticles,
          {
            query: query,
          },
        );
      setSearchResults(searchResponse['search-results'].slice(0, MAX_NUM_RESULTS));
    } catch {
      setIsSearchErrored(true);
    }

    setIsSearchLoading(false);

    SegmentClient.sendSegmentTrackEvent('Contact Us - Help Center Search', {
      searchQuery: query,
    });
  };

  const [debouncedPerformSearch] = useDebounced(performSearch, 500, {
    leading: false,
    trailing: true,
  });

  const renderSearchResults = () => {
    if (isSearchLoading) {
      return (
        <SearchResults>
          <Paragraph size='md' color='secondary'>
            Searching...
          </Paragraph>
        </SearchResults>
      );
    }
    if (isSearchErrored) {
      return (
        <SearchResults>
          There was an error performing this search. Please try again later.
        </SearchResults>
      );
    } else if (searchResults.length > 0) {
      return (
        <SearchResults>
          {searchResults.map((searchResult, index) => (
            <SearchResultRow
              key={`contact-us__search-result-${index}}`}
              onClick={() => {
                window.open(
                  `https://help.withpersona.com/articles/${searchResult['contentful-id']}`,
                  '_blank',
                );
              }}
            >
              <DocumentIcon />
              {searchResult.title}
            </SearchResultRow>
          ))}
          <Button
            size='sm'
            variant='basic'
            disabled={searchResults.length === 0}
            onClick={() => {
              window.open(`https://help.withpersona.com/search_results?q=${searchQuery}`, '_blank');
            }}
          >
            See all results
          </Button>
        </SearchResults>
      );
    } else {
      return null;
    }
  };

  const handleSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      window.open(`https://help.withpersona.com/search_results?q=${searchQuery}`, '_blank');
    }
  };

  return (
    <SearchWrapper>
      <InputGroup
        id='contact-us__search-wrapper'
        size='md'
        leftIcon={<SearchIcon />}
        rightButton={
          <IconButton
            id='contact-us__search-clear-button'
            aria-label='contact-us__search-clear-button'
          >
            <XIcon
              onClick={() => {
                setSearchQuery('');
                setSearchResults([]);
              }}
            />
          </IconButton>
        }
      >
        <InputStyled
          id='contact-us__search-input'
          name='contact-us__search-input'
          aria-label='contact-us__search-input'
          aria-describedby='contact-us__search-input'
          ref={searchInputRef}
          value={searchQuery}
          placeholder='Search for an article or ask a question...'
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedPerformSearch(e.target.value);
          }}
          onKeyUp={(e) => handleSearchKeyUp(e)}
        />
      </InputGroup>
      {renderSearchResults()}
    </SearchWrapper>
  );
};

interface HelpCenterArticleSearchResponse {
  'search-results': HelpCenterArticle[];
}

interface HelpCenterArticle {
  'contentful-id': string;
  title: string;
  slug: string;
  'content-type': string;
}

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  margin: auto;
  padding: 0px ${Spacing[4]};
`;

const InputStyled = styled(Input)`
  background-color: ${Color.CoreSlate50};
`;

const SearchResults = styled.div`
  position: absolute;
  top: 34px;
  left: 16px;
  width: calc(100% - 32px);
  padding: ${Spacing[2]} ${Spacing[3]};
  display: flex;
  flex-direction: column;
  row-gap: ${Spacing[2]};
  background-color: ${Color.PrimaryWhite};
  border: 1px solid ${Color.BorderDivider};
  border-radius: ${BorderRadius.Sm};
  box-shadow: 1px 2px 1px ${Color.BorderHairline};
  z-index: 2;
`;

const SearchResultRow = styled.div`
  display: flex;
  column-gap: ${Spacing[2]};
  align-items: center;
  padding: ${Spacing[1]};
  cursor: pointer;
  border-radius: ${BorderRadius.Sm};

  :hover {
    background-color: ${Color.CorePurple100};
  }
`;

export default DashboardContactUsSearch;
